"use client";
import Image from 'next/image';
import { MenuItem, GlobalData } from '@/lib/strapi/types';
import { COMPANY_NAME } from '@/lib/constants';
import { useTheme } from '@/hooks/useTheme';
import HamburgerMenu from './HamburgerMenu';
import { usePathname } from 'next/navigation';
import { toMediaProxyPath } from '@/components/toMediaProxyPath';
import { useEffect, useState } from 'react';

interface NavbarProps {
    global: GlobalData | null;
    layout: boolean | null;
}

type NavItem = MenuItem & {
  _key: string;
  children?: NavItem[];
};

const normalizeMenuKey = (value: unknown, fallback: string): string => {
  // Strapi component ids are numeric, while parentId is stored as string in CMS.
  // Normalize both sides to string so parent-child matching is reliable.
  // This also keeps generated fallback keys (e.g. item-0) compatible.
  if (value === null || typeof value === 'undefined') return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

export default function Navbar({ global, layout }: NavbarProps) {
  const pathname = usePathname();
  const [isClient, setIsClient] = useState(false);
  // Keep explicit open state so submenu parents can open on tap/click on touch devices (no hover).
  const [openMenuKeys, setOpenMenuKeys] = useState<Set<string>>(new Set());
  const logoLightSrc = toMediaProxyPath(global?.logoLight?.url) || '/undefined-logo-light.svg';
  const logoDarkSrc = toMediaProxyPath(global?.logoDark?.url) || '/undefined-logo-dark.svg';
  const { isDarkMode, toggleTheme } = useTheme(logoLightSrc, logoDarkSrc);
  
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  if (typeof layout === 'undefined') {
    layout = false;
  }
  if (layout && isClient && pathname === '/') {
    return null;
  }
  // Helper for analytics attributes from Strapi
  const getAnalytics = (fieldName: string): Record<string, string> => {
    const key = `${fieldName}_analytics`;
    const dyn = (global as unknown as Record<string, unknown>) || {};
    const val = dyn[key] as Record<string, string> | undefined;
    return val || {};
  };

  // Helper to determine link properties
  const getLinkProps = (item: MenuItem) => {
    const props: Record<string, string> = {
      href: item.link || '#',
    };
    if (item.openInNewTab || (item.link && (item.link.startsWith('http://') || item.link.startsWith('https://')))) {
      props.target = '_blank';
      props.rel = 'noopener noreferrer';
    }
    return props;
  };

  // Toggle one submenu's click-open state (used by touch + click interactions).
  const toggleOpenMenu = (key: string) => {
    setOpenMenuKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Close all click-open submenus, e.g. after selecting a leaf link.
  const closeAllMenus = () => {
    setOpenMenuKeys(new Set());
  };

  // Helper component for rendering menu items recursively
  const renderMenuItem = (item: NavItem, depth = 0) => {
    const isTopLevel = depth === 0;
    const isBlankLabel = !item.label || item.label.trim().length === 0;
    const hasChildren = item.children && item.children.length > 0;
    if (hasChildren) {
      const isOpen = openMenuKeys.has(item._key);
      return (
        <li key={item._key} className="relative group">
          <button
            className={`${isTopLevel ? 'nav-menu-trigger-top' : 'nav-menu-trigger-child'} ${isBlankLabel ? 'nav-menu-trigger-icon-only' : ''}`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            onClick={() => toggleOpenMenu(item._key)}
            {...(item.label_analytics || {})}
          >
            <span className="flex items-center gap-2 min-w-0">
              {item.icon && <i className={item.icon}></i>}
              <span className={isTopLevel ? 'nav-menu-trigger-label-top' : 'nav-menu-trigger-label-child'}>
                {item.label || ''}
              </span>
            </span>
            <i className={`fas ${depth === 0 ? 'fa-chevron-down' : 'fa-chevron-right'} text-xs opacity-80`}></i>
          </button>
          {/* Dropdown */}
          <ul 
            className={`absolute ${depth === 0 ? 'left-1/2 -translate-x-1/2 top-full mt-3' : 'left-full top-0 ml-2'} nav-menu-panel transition-all duration-200 z-50 ${isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-1'} group-hover:opacity-100 group-hover:visible group-hover:translate-y-0`}
          >
            {(item.children || [])
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((child) => (
                renderMenuItem(child as NavItem, depth + 1)
              ))}
          </ul>
        </li>
      );
    }
    return (
      <li key={item._key}>
        <a
          {...getLinkProps(item)}
          className={isTopLevel ? 'nav-menu-link-top' : 'nav-menu-link-child'}
          onClick={closeAllMenus}
          {...(item.label_analytics || {})}
        >
          {item.icon && <i className={item.icon}></i>}
          {item.label}
          {item.openInNewTab && <i className="fas fa-external-link-alt text-xs ml-1"></i>}
        </a>
      </li>
    );
  };

  // Build hierarchical menu tree from flat array with parentId
  const buildMenuTree = (items: MenuItem[]): NavItem[] => {
    const itemMap = new Map<string, NavItem>();
    const rootItems: NavItem[] = [];

    items.forEach((item, index) => {
      const key = normalizeMenuKey(item.id, `item-${index}`);
      const itemWithChildren: NavItem = { ...item, _key: key, children: [] };
      itemMap.set(key, itemWithChildren);
    });

    items.forEach((item, index) => {
      const itemKey = normalizeMenuKey(item.id, `item-${index}`);
      const currentItem = itemMap.get(itemKey);
      if (!currentItem) return;
      if (item.parentId) {
        const parent = itemMap.get(normalizeMenuKey(item.parentId, ''));
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(currentItem);
        } else {
          rootItems.push(currentItem);
        }
      } else {
        rootItems.push(currentItem);
      }
    });
    rootItems.forEach(item => {
      if (item.children) {
        item.children.sort((a, b) => (a.order || 0) - (b.order || 0));
      }
    });
    return rootItems.sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const menuTree = buildMenuTree(global?.menuItems || []);

  return (
    <nav
      className="navbar-root sticky top-0 z-50 py-5 shadow-lg border-b-2"
    >
      <div className="max-w-6xl mx-auto px-5 flex justify-between items-center">
        <div className="flex items-center gap-3">
          {/* Small Nav Logo */}
          {logoLightSrc && (
            <Image
              src={logoLightSrc}
              alt={global?.logoAlt || `${global?.companyName} logo`}
              width={40}
              height={40}
              className="logo logo-light nav-logo"
              {...getAnalytics('logoLight')}
            />
          )}
          {logoDarkSrc && (
            <Image
              src={logoDarkSrc}
              alt={global?.logoAlt || `${global?.companyName} logo`}
              width={40}
              height={40}
              className="logo logo-dark nav-logo"
              {...getAnalytics('logoDark')}
            />
          )}
          <div className="navbar-company-name text-2xl font-bold" {...getAnalytics('companyName')}>
            {global?.companyName || COMPANY_NAME}
          </div>
        </div>
        <div className="flex items-center gap-8">
          <ul className="hidden md:flex gap-8">
            {menuTree.map((item) => renderMenuItem(item))}
          </ul>
          <button
            onClick={toggleTheme}
            className="navbar-theme-toggle w-11 h-11 rounded-full flex items-center justify-center text-lg transition-all duration-300 hover:scale-110 border-2"
          >
            <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
          </button>
        </div>
      </div>
      {/* Hamburger menu for mobile */}
      <div className="md:hidden">
        <HamburgerMenu menuItems={global?.menuItems || []} />
      </div>
    </nav>
  );
}
